import React, { useState } from "react";

import { graphql } from "relay-runtime";

import { useSyncedPersistentState } from "~/actionItems/hooks/useSyncedPersistentState";
import { getIntegrationForKey } from "~/integrations/actionItemIntegration";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Input } from "~/ui/legacyFellowIndex";

import type { CreateOrImportProps } from "../actionItemIntegration/actionItemLinkingModal";
import { BoardSelector } from "./selectors/MondayBoardSelector";
import { ColumnsSelectors } from "./selectors/MondayColumnsSelectors";
import { GroupSelector } from "./selectors/MondayGroupSelector";
import { mondayStore } from "./store";

import type { MondayCreateNewExternalTicketPaneMutation } from "~/__generated__/MondayCreateNewExternalTicketPaneMutation.graphql";

import mondayModal from "./css/mondayModal.module.css";

const createExternalTicket = graphql`
	mutation MondayCreateNewExternalTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$title: String!
		$destinationInfo: JSON!
	) {
		sendActionItemToIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				title: $title
				destinationInfo: $destinationInfo
				integrationProvider: MONDAY
			}
		) {
			clientMutationId
		}
	}
`;

function linkToMonday(
	blockKey: string,
	noteId: string,
	title: string,
	boardId: string,
	groupId: string,
	groupName: string,
	boardName: string,
	assigneeColumnId?: string,
	statusColumnId?: string,
	dueDateColumnId?: string,
) {
	return commitMutationPromise<MondayCreateNewExternalTicketPaneMutation>(environment, {
		mutation: createExternalTicket,
		variables: {
			blockKey,
			noteId,
			title,
			destinationInfo: {
				/* eslint-disable camelcase */
				board_id: boardId,
				group_id: groupId,
				group_name: groupName,
				board_name: boardName,
				assignee_column_id: assigneeColumnId,
				status_column_id: statusColumnId,
				due_date_column_id: dueDateColumnId,
				/* eslint-enable camelcase */
			},
		},
	});
}

export function CreateNewExternalTicketPane({
	setCanSubmit,
	registerSubmit,
	initialText,
	blockKey,
	noteId,
}: CreateOrImportProps) {
	const [boardId, setBoardId] = useSyncedPersistentState<string | undefined>(mondayStore, "boardId", undefined);
	const [groupId, setGroupId] = useState<string>(mondayStore.get("groupId") ?? undefined);
	const [groupName, setGroupName] = useState<string>(mondayStore.get("groupName") ?? undefined);
	const [boardName, setBoardName] = useState<string>(mondayStore.get("boardName") ?? undefined);
	const [assigneeColumnId, setAssigneeColumnId] = useState<string>(mondayStore.get("assigneeColumnId") ?? undefined);
	const [statusColumnId, setStatusColumnId] = useState<string>(mondayStore.get("statusColumnId") ?? undefined);
	const [dueDateColumnId, setDueDateColumnId] = useState<string>(mondayStore.get("dueDateColumnId") ?? undefined);
	const [title, setTitle] = useState(initialText || "");

	const onTitleChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
		// @ts-ignore
		setTitle(e.target.value);
	}, []);

	const { displayName } = getIntegrationForKey("MONDAY");

	const submit = React.useCallback(async () => {
		if (!title || !boardId || !groupId || !groupName || !boardName) {
			throw new Error("Submission validation step failed");
		}

		return linkToMonday(
			blockKey,
			noteId,
			title,
			boardId,
			groupId,
			groupName,
			boardName,
			assigneeColumnId,
			statusColumnId,
			dueDateColumnId,
		).then(() => true);
	}, [
		blockKey,
		noteId,
		title,
		boardId,
		groupId,
		assigneeColumnId,
		statusColumnId,
		dueDateColumnId,
		groupName,
		boardName,
	]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		setCanSubmit(Boolean(boardId && groupId && title));
	}, [setCanSubmit, boardId, groupId, title]);

	return (
		<div className={mondayModal.container}>
			<label className={mondayModal.board}>
				<div className={mondayModal.label}>Board</div>
				<BoardSelector boardId={boardId} setBoardId={setBoardId} setBoardName={setBoardName} />
			</label>
			<label className={mondayModal.group}>
				<div className={mondayModal.label}>Group</div>
				<GroupSelector boardId={boardId} groupId={groupId} setGroupId={setGroupId} setGroupName={setGroupName} />
			</label>
			<ColumnsSelectors
				boardId={boardId}
				assigneeColumnId={assigneeColumnId}
				dueDateColumnId={dueDateColumnId}
				statusColumnId={statusColumnId}
				setAssigneeColumnId={setAssigneeColumnId}
				setDueDateColumnId={setDueDateColumnId}
				setStatusColumnId={setStatusColumnId}
				showLabels={true}
			/>
			<label className={mondayModal.title}>
				<div className={mondayModal.label}>Title</div>
				<Input
					name="monday_task_title"
					className={mondayModal.titleInput}
					type="text"
					value={title}
					onChange={onTitleChange}
				/>
			</label>
			<div className={mondayModal.help}>
				The task name, due date, assignee, and status will automatically sync with the {displayName} columns selected.
			</div>
		</div>
	);
}
